import { useEffect, useState, useCallback } from 'react';
import { logger } from '../utils/logger';
import { Link } from 'react-router-dom';
import { Radio, Calendar, Clock, Lock, Crown, Users, Play, ExternalLink, Video } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { WebRTCViewer, StreamCard, EventCard } from '../components/streaming';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getEmbedUrl = (url, platform) => {
  if (platform === 'youtube') {
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    if (url.includes('youtube.com/live/')) {
      const videoId = url.split('youtube.com/live/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
  }
  if (platform === 'twitch') {
    if (url.includes('twitch.tv/')) {
      const channel = url.split('twitch.tv/')[1]?.split('/')[0]?.split('?')[0];
      return `https://player.twitch.tv/?channel=${channel}&parent=${window.location.hostname}`;
    }
  }
  return url;
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const isUpcoming = (dateStr) => {
  return new Date(dateStr) > new Date();
};

export default function LiveEventsPage() {
  const { isAuthenticated, isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [activeStreams, setActiveStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedStream, setSelectedStream] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/events`);
      setEvents(res.data);
      
      const liveEvent = res.data.find(e => e.is_live && !e.locked);
      if (liveEvent) setSelectedEvent(liveEvent);
      
      if (isAuthenticated) {
        const subRes = await axios.get(`${API_URL}/subscription`);
        setSubscription(subRes.data);
      }
    } catch (error) {
      logger.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchActiveStreams = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/streams/active`);
      setActiveStreams(res.data);
      
      if (res.data.length > 0 && !selectedEvent && !selectedStream) {
        setSelectedStream(res.data[0]);
      }
    } catch (error) {
      logger.error('Failed to fetch streams:', error);
    }
  }, [selectedEvent, selectedStream]);

  useEffect(() => {
    fetchEvents();
    fetchActiveStreams();
    
    const interval = setInterval(fetchActiveStreams, 10000);
    return () => clearInterval(interval);
  }, [fetchEvents, fetchActiveStreams]);

  const handleSelectStream = (stream) => {
    setSelectedStream(stream);
    setSelectedEvent(null);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setSelectedStream(null);
  };

  const isPremium = subscription?.tier === 'premium';
  const liveEvents = events.filter(e => e.is_live);
  const upcomingEvents = events.filter(e => !e.is_live && isUpcoming(e.scheduled_at));
  const pastEvents = events.filter(e => !e.is_live && !isUpcoming(e.scheduled_at));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="main-content pt-24" data-testid="live-events-page">
      <div className="container py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center">
              <Radio className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Live Events</h1>
              <p className="text-gray-400">Live streams & scheduled events</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Link to="/studio" className="btn-secondary flex items-center gap-2" data-testid="go-studio">
                <Video className="w-4 h-4" /> Broadcast Studio
              </Link>
            )}
            {!isPremium && (
              <Link to="/subscription" className="btn-primary flex items-center gap-2" data-testid="upgrade-premium">
                <Crown className="w-4 h-4" /> Unlock Premium
              </Link>
            )}
          </div>
        </div>

        {/* Active WebRTC Streams */}
        {activeStreams.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
              <span className="text-red-400 font-semibold">STREAMING NOW</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {activeStreams.map(stream => (
                <StreamCard
                  key={stream.id}
                  stream={stream}
                  isSelected={selectedStream?.id === stream.id}
                  onSelect={() => handleSelectStream(stream)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Live Now Banner (embed-based events) */}
        {liveEvents.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
              <span className="text-red-400 font-semibold">LIVE NOW</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {liveEvents.map(event => (
                <EventCard
                  key={event.id}
                  event={event}
                  isSelected={selectedEvent?.id === event.id}
                  onSelect={handleSelectEvent}
                />
              ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Stream Player */}
          <div className="lg:col-span-2">
            {selectedStream ? (
              <div className="glass-card p-6 sticky top-24" data-testid="webrtc-player">
                <WebRTCViewer 
                  streamId={selectedStream.id} 
                  onClose={() => setSelectedStream(null)} 
                />
                
                <div className="flex items-start justify-between mt-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-red-500 rounded text-xs font-bold flex items-center gap-1">
                        <Radio className="w-3 h-3" /> LIVE
                      </span>
                      <span className="flex items-center gap-1 text-sm text-gray-400">
                        <Users className="w-4 h-4" /> {selectedStream.viewer_count} watching
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold">{selectedStream.title}</h2>
                    <p className="text-gray-400 mt-2">@DaRealQJones is streaming live</p>
                  </div>
                </div>
              </div>
            ) : selectedEvent ? (
              <div className="glass-card p-6 sticky top-24" data-testid="stream-player">
                <div className="aspect-video rounded-lg overflow-hidden bg-black mb-4">
                  <iframe
                    src={getEmbedUrl(selectedEvent.stream_url, selectedEvent.platform)}
                    title={selectedEvent.title}
                    className="w-full h-full"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                </div>
                
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {selectedEvent.is_live && (
                        <span className="px-2 py-0.5 bg-red-500 rounded text-xs font-bold flex items-center gap-1">
                          <Radio className="w-3 h-3" /> LIVE
                        </span>
                      )}
                      {selectedEvent.is_premium && (
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs font-bold flex items-center gap-1">
                          <Crown className="w-3 h-3" /> Premium
                        </span>
                      )}
                    </div>
                    <h2 className="text-2xl font-bold">{selectedEvent.title}</h2>
                    <p className="text-gray-400 mt-2">{selectedEvent.description}</p>
                  </div>
                  <a 
                    href={selectedEvent.stream_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-white/10 rounded-lg"
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-5 h-5 text-gray-400" />
                  </a>
                </div>
              </div>
            ) : (
              <div className="glass-card p-12 text-center sticky top-24">
                <Radio className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">Select an event to watch</p>
              </div>
            )}
          </div>

          {/* Event List */}
          <div className="space-y-6">
            {/* Upcoming Events */}
            {upcomingEvents.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-cyan-400" /> Upcoming
                </h3>
                <div className="space-y-3">
                  {upcomingEvents.map(event => (
                    <div 
                      key={event.id}
                      className="glass-card p-4"
                      data-testid={`upcoming-event-${event.id}`}
                    >
                      <div className="flex gap-3">
                        <div className="relative w-20 h-14 flex-shrink-0">
                          <img 
                            src={event.thumbnail_url} 
                            alt={event.title}
                            className="w-full h-full object-cover rounded"
                          />
                          {event.is_premium && (
                            <div className="absolute top-1 right-1">
                              <Crown className="w-3 h-3 text-yellow-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm truncate">{event.title}</h4>
                          <p className="text-xs text-cyan-400 flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(event.scheduled_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Past Events */}
            {pastEvents.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Play className="w-5 h-5 text-gray-400" /> Past Events
                </h3>
                <div className="space-y-3">
                  {pastEvents.slice(0, 5).map(event => (
                    <div 
                      key={event.id}
                      onClick={() => !event.locked && handleSelectEvent(event)}
                      className={`glass-card p-4 cursor-pointer transition-all ${
                        event.locked ? 'opacity-60' : 'hover:border-white/20'
                      }`}
                      data-testid={`past-event-${event.id}`}
                    >
                      <div className="flex gap-3">
                        <div className="relative w-20 h-14 flex-shrink-0">
                          <img 
                            src={event.thumbnail_url} 
                            alt={event.title}
                            className="w-full h-full object-cover rounded"
                          />
                          {event.locked && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded">
                              <Lock className="w-4 h-4 text-yellow-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm truncate">{event.title}</h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(event.scheduled_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {events.length === 0 && (
              <div className="empty-state glass-card py-12">
                <Calendar className="empty-state-icon w-12 h-12" />
                <h3 className="font-semibold mb-1">No events scheduled</h3>
                <p className="text-sm text-gray-400">Check back soon!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
